/**
 * KeyFieldsGrid - Reusable 2-column grid for entity key fields.
 *
 * For CREATED/DELETED: renders all fields with values.
 * For UPDATED/SAME: renders changed fields normally, unchanged fields
 * are contributed to the parent's "show more" section via the
 * unchangedContent render pattern.
 */

import { cn } from '@/lib/utils';
import type { ChangeType } from '@/services/api/generated/crossMatterChangesService';

import type { EntityChanges } from '../utils/useEntityChanges';
import type { TransferChecklist } from '../utils/useTransferChecklist';
import { EntityField } from './EntityField';
import { TransferCheckbox } from './TransferCheckbox';

export interface FieldConfig {
  key: string;
  label: string;
  isDate?: boolean;
  isEnum?: boolean;
}

interface KeyFieldsGridProps {
  fields: FieldConfig[];
  data: Record<string, unknown>;
  changes: EntityChanges;
  checklist: TransferChecklist;
  changeType?: ChangeType;
}

function renderFieldGrid(
  fields: FieldConfig[],
  data: Record<string, unknown>,
  changes: EntityChanges,
  checklist: TransferChecklist,
) {
  return (
    <div className="grid grid-cols-2 gap-x-4 gap-y-0.5">
      {fields.map((f) => {
        const isChanged = changes.isFieldChanged(f.key);
        return (
          <div
            key={f.key}
            className={cn(
              'flex items-start',
              checklist.showCheckboxes && isChanged && 'gap-2',
            )}
          >
            <div className="flex-1 min-w-0">
              <EntityField
                label={f.label}
                value={data[f.key]}
                fieldKey={f.key}
                changes={changes}
                isDate={f.isDate}
                isEnum={f.isEnum}
              />
            </div>
            {checklist.showCheckboxes && isChanged && (
              <TransferCheckbox
                checked={checklist.isChecked(f.key)}
                onToggle={() => checklist.toggle(f.key)}
                className="mt-2.5"
              />
            )}
          </div>
        );
      })}
    </div>
  );
}

export const KeyFieldsGrid: React.FC<KeyFieldsGridProps> = ({
  fields,
  data,
  changes,
  checklist,
  changeType,
}) => {
  const visibleFields = fields.filter((f) => {
    const val = data[f.key];
    const hasValue = val !== null && val !== undefined && val !== '';
    return hasValue || changes.isFieldChanged(f.key);
  });

  if (visibleFields.length === 0) return null;

  // For CREATED/DELETED (or no changeType), show everything
  const showAll =
    !changeType || changeType === 'CREATED' || changeType === 'DELETED';

  if (showAll) {
    return renderFieldGrid(visibleFields, data, changes, checklist);
  }

  // For UPDATED/SAME: only render changed fields here
  const changedFields = visibleFields.filter((f) =>
    changes.isFieldChanged(f.key),
  );

  if (changedFields.length === 0) return null;

  return renderFieldGrid(changedFields, data, changes, checklist);
};

/**
 * KeyFieldsGridUnchanged - Renders only the unchanged fields from a KeyFieldsGrid.
 *
 * Use this inside a "Show More" collapsible to display unchanged fields
 * for UPDATED/SAME entities.
 */
export const KeyFieldsGridUnchanged: React.FC<KeyFieldsGridProps> = ({
  fields,
  data,
  changes,
  checklist,
}) => {
  const unchangedFields = fields.filter((f) => {
    const val = data[f.key];
    const hasValue = val !== null && val !== undefined && val !== '';
    return hasValue && !changes.isFieldChanged(f.key);
  });

  if (unchangedFields.length === 0) return null;

  return renderFieldGrid(unchangedFields, data, changes, checklist);
};
